import { type FormEvent, type ReactNode, useMemo, useState } from 'react';
import { useMachine } from '@xstate/react';
import { useQuery } from '@tanstack/react-query';
import { analysisMachine } from './machines/analysisMachine';
import { api, normalizeDomain } from './lib/api';
import type { ModuleStatus } from './lib/analysis';
import { CompetitorDiscoveryPanel, CreativePanel, PaidAdsPanel, SocialPanel, WatchlistPanel } from './components/IntelligenceModules';
import { DataQualityPanel } from './components/DataQualityPanel';

const nav=['总览','竞品发现','流量','销售','经营效率','SEO','付费广告','社媒','产品','内容素材','变化记录','竞品监控'];
const migrated=new Set(nav);
const moduleNames:Record<string,string>={website:'官网',catalog:'商品目录',metrics:'SEO / 流量 / 销售模型',positioning:'DeepSeek 商业定位',social:'社媒',ads:'广告',strategy:'内容策略',snapshot:'历史快照'};
const labels:Record<ModuleStatus,string>={pending:'等待',running:'进行中',success:'完成',partial:'部分可用',error:'受限'};

function money(v:unknown,c='USD'){
  return typeof v==='number'&&Number.isFinite(v)
    ?new Intl.NumberFormat('zh-CN',{style:'currency',currency:c,maximumFractionDigits:0}).format(v)
    :'—';
}
function num(v:unknown,digits=0){
  return typeof v==='number'&&Number.isFinite(v)
    ?new Intl.NumberFormat('zh-CN',{maximumFractionDigits:digits}).format(v)
    :'—';
}
function pct(v:unknown,digits=0){
  return typeof v==='number'&&Number.isFinite(v)?(v*100).toFixed(digits)+'%':'—';
}
function zhText(v:{zh?:string;en?:string}|null|undefined){return v?.zh||v?.en||'—';}
function Metric({label,value,note}:{label:string;value:ReactNode;note?:ReactNode}){
  return <div className="metric-card"><div className="metric-label">{label}</div><div className="metric-value">{value}</div>{note&&<div className="metric-note">{note}</div>}</div>;
}
function DataTag({type}:{type:'observed'|'estimated'|'derived'}){
  const text={observed:'已观测',estimated:'预估',derived:'推导'}[type];
  return <span className={'data-tag data-'+type}>{text}</span>;
}
function EmptyModule({title,detail}:{title:string;detail:string}){
  return <section className="panel"><div className="empty-state"><strong>{title}</strong><p>{detail}</p></div></section>;
}
type TrendLike={classification?:string;status?:string;points?:Array<{period:string;value:number|null}>;latest?:{period:string;value:number|null;previousPct:number|null;momPct:number|null;yoyPct:number|null}|null};
function delta(v:number|null|undefined){
  if(v==null||!Number.isFinite(v))return '—';
  return (v>0?'+':'')+v.toFixed(1)+'%';
}
function TrendMetricCard({title,trend,format}:{title:string;trend?:TrendLike;format:(v:number|null)=>string}){
  const points=trend?.points||[];
  const values=points.map(x=>x.value).filter((x):x is number=>typeof x==='number'&&Number.isFinite(x));
  const max=values.length?Math.max(...values.map(Math.abs),1):1;
  const latest=trend?.latest||null;
  return <div className="trend-card">
    <div className="trend-card-head"><span>{title}</span><span className={'data-tag data-'+(trend?.classification||'observed')}>{trend?.classification==='estimated'?'预估':trend?.classification==='derived'?'推导':'已观测'}</span></div>
    <strong>{format(latest?.value??null)}</strong>
    <div className="trend-deltas"><span>环比 {delta(latest?.momPct)}</span><span>同比 {delta(latest?.yoyPct)}</span></div>
    <div className="trend-bars">{points.slice(-12).map((p,i)=><div className="trend-bar-wrap" key={p.period+'-'+i} title={p.period+' · '+format(p.value)}><span style={{height:(p.value==null?4:Math.max(8,Math.round(Math.abs(p.value)/max*52)))+'px'}}/></div>)}</div>
    <div className="trend-status">{trend?.status==='ready'?points.length+' 个有效周期':trend?.status==='no_data'?'暂无数据':'历史周期不足；不会补造环比/同比'}</div>
  </div>;
}

export default function App(){
  const [state,send]=useMachine(analysisMachine);
  const [input,setInput]=useState('kailashenergy.com');
  const [active,setActive]=useState('总览');
  const [historyRange,setHistoryRange]=useState<'30d'|'90d'|'1y'|'all'>('all');
  const [historyGrain,setHistoryGrain]=useState<'daily'|'monthly'|'yearly'>('daily');
  const result=state.context.result;
  const running=state.matches('running');
  const domain=result?.domain||normalizeDomain(input);

  const history=useQuery({
    queryKey:['history',domain,historyRange,historyGrain],
    queryFn:()=>api.history(domain,{range:historyRange,grain:historyGrain}),
    enabled:Boolean(domain&&result),
    staleTime:10000
  });

  const analyzeDomain=(value:string)=>{
    const d=normalizeDomain(value);
    if(!d)return;
    setInput(d);
    setActive('总览');
    send({type:'ANALYZE',domain:d});
  };

  const submit=(e:FormEvent)=>{
    e.preventDefault();
    analyzeDomain(input);
  };

  const p=result?.catalog;
  const currency=p?.currency||p?.priceRange?.currency||'USD';
  const productCount=p?.catalogComplete===true?p.count:null;
  const catalogCached=Boolean(p&&result?.modules.catalog?.error?.startsWith('DATED_CACHED_CATALOG'));
  const catalogTime=catalogCached?result?.modules.catalog?.error?.split('DATED_CACHED_CATALOG: ')[1]||null:null;
  const nextAttempt=result?.modules.website?.error?.match(/SOURCE_COOLDOWN_429_UNTIL_([^\s]+)/)?.[1]||null;
  const retryTime=nextAttempt&&Number.isFinite(Date.parse(nextAttempt))
    ?new Date(nextAttempt).toLocaleString('zh-CN'):'供应方解除限制后';
  const lastHistoricalProduct=(history.data?.products||[]).find((row)=>{
    if(!row||typeof row!=='object')return false;
    const x=row as {product_count?:unknown;payload?:{catalogComplete?:boolean}};
    return typeof x.product_count==='number'&&x.product_count>0&&x.payload?.catalogComplete===true;
  }) as {product_count:number;observed_at?:string;currency?:string}|undefined;
  const completed=useMemo(
    ()=>result?Object.values(result.modules).filter(x=>x.status==='success').length:0,
    [result]
  );
  const metrics=result?.metrics;
  const traffic=metrics?.traffic;
  const revenue=metrics?.revenue;
  const economics=metrics?.economics;
  const seo=metrics?.seo; 
  const independentSearch=result?.search?.status==='observed'?result.search:null;
  const positioning=result?.positioning;
  const trafficHistory=history.data?.traffic?.length||0;
  const revenueHistory=history.data?.revenue?.length||0;
  const economicsHistory=history.data?.economics?.length||0;
  const seoHistory=history.data?.seo?.length||0;
  const seoCompletion=seo?.pagesSampled
    ?(seo.pagesWithTitles+seo.pagesWithDescriptions+seo.pagesWithH1)/(seo.pagesSampled*3)
    :null;

  return <div className="app-shell">
    <aside className="sidebar">
      <div className="brand-block"><div className="brand-mark">IC</div><div><strong>Insight Compass</strong><span>DTC 品牌经营情报 · V2</span></div></div>
      <div className="nav-label">情报分析</div>
      <nav>{nav.map(item=><button key={item} className={active===item?'nav-item active':'nav-item'} onClick={()=>setActive(item)}>
        <span>{item}</span>{!migrated.has(item)&&<small>迁移中</small>}
      </button>)}</nav>
      <div className="sidebar-foot"><span>V2 Alpha</span><span>Single Analysis Runtime</span></div>
    </aside>

    <main className="workspace">
      <header className="topbar">
        <div><h1>{result?.website.website.title||'品牌分析'}</h1><p>{result?.domain||'输入一个 DTC 独立站域名开始分析'}</p></div>
        <span className="alpha-badge">V2 ALPHA</span>
      </header>

      <section className="content">
        <form className="analysis-form" onSubmit={submit}>
          <div className="domain-input-wrap"><span>◎</span><input aria-label="竞品域名" value={input} onChange={e=>setInput(e.target.value)} placeholder="example.com"/></div>
          <button className="primary-button" type="submit">{running?'重新开始分析':result?'重新分析':'开始分析'}</button>
        </form>
        <div className="runtime-note"><span className="dot"/>{running&&result?'正在刷新；页面继续保留上一轮已验证数据，直到新 Run 完成。':'新分析会自动取消旧请求；API 返回必须通过 Zod 校验后才能进入页面与数据库。'}</div>

        {active==='总览'&&<>
          <div className="page-heading"><h2>经营总览</h2><p>区分已观测、预估与推导数据；不使用临时 Shopify 250 条结果冒充完整目录。</p></div>
          <DataQualityPanel result={result}/>
          <div className="metric-grid">
            <Metric label="建站平台" value={result?.website.technology?.platform||'—'} note={<><DataTag type="observed"/> {result?.modules.website.status==='partial'?'商品接口信号（首页受限）':'官网技术信号'}</>}/>
            <Metric label={catalogCached?'最近缓存商品数':'公开商品数'} value={productCount??(running?'校验中…':'—')} note={<><DataTag type="observed"/> {catalogCached?'已缓存观测 · '+(catalogTime?new Date(catalogTime).toLocaleString('zh-CN'):'日期未知')+'，不是实时数据':productCount!=null?'本次完整目录校验':'未取得本次完整目录，历史数据另列'}</>}/>
            <Metric label="月流量 · Base" value={traffic?num(traffic.base):'—'} note={<><DataTag type="estimated"/> {traffic?'Low '+num(traffic.low)+' · High '+num(traffic.high):'等待 Traffic 模型'}</>}/>
            <Metric label="历史快照" value={history.isLoading?'读取中…':history.data?.snapshots?.length??'—'} note="Supabase"/>
          </div>

          {result?.modules.website.status==='partial'&&lastHistoricalProduct&&<section className="panel" aria-label="历史商品参考">
            <div className="panel-title-row"><div><h2>最近一次已验证的商品目录快照 · 历史参考</h2>
              <p>采集日期：{lastHistoricalProduct.observed_at?new Date(lastHistoricalProduct.observed_at).toLocaleString('zh-CN'):'未知'}。与本次采集分开，不表示当前在售商品数。</p></div><DataTag type="observed"/></div>
            <div className="metric-grid">
              <Metric label="历史公开商品数" value={num(lastHistoricalProduct.product_count)} note="历史已保存快照 · 非本次实时结果"/>
            </div>
            <button className="primary-button" type="button" onClick={()=>setActive('变化记录')}>查看历史趋势</button>
          </section>}

          {result?.modules.website.status==='partial'&&(history.data?.traffic?.length??0)>0&&(()=>{
            const previous=((history.data?.traffic||[]) as unknown[]).find(row=>
              typeof row==='object'&&row!==null&&typeof (row as {base?:unknown}).base==='number'
            ) as {base:number;observed_at?:string;model_version?:string}|undefined;
            if(!previous)return null;
            return <section className="panel" aria-label="历史流量模型估算">
              <div className="panel-title-row"><div><h2>历史流量估算 · 非实时</h2>
                <p>模型版本：{previous.model_version||'未知'} · 估算保存日期：{previous.observed_at?new Date(previous.observed_at).toLocaleString('zh-CN'):'未知'}</p>
                <p>仅展示旧模型曾经输出的预测，不代表真实 Analytics 流量，也不能替代本次分析。</p>
              </div><DataTag type="estimated"/></div>
              <div className="metric-grid"><Metric label="历史月流量 Base（模型估算）" value={num(previous.base)} note="旧预测 · 未经真实数据验证"/></div>
            </section>;
          })()}

          {positioning&&<section className="panel">
            <div className="panel-title-row"><div><h2>商业定位摘要</h2><p>基于官网、完整商品目录与公开内容由 DeepSeek 推导；不是竞品官方自述。</p></div><DataTag type="derived"/></div>
            <div className="positioning-grid">
              <div><span>主营品类</span><strong>{zhText(positioning.category)}</strong><p>{positioning.subcategories.map(zhText).slice(0,4).join(' · ')||'—'}</p></div>
              <div><span>核心目标人群</span><strong>{zhText(positioning.audience)}</strong></div>
              <div className="wide"><span>核心价值主张</span><strong>{zhText(positioning.valueProp)}</strong></div>
            </div>
            <div className="positioning-lists">
              <div><span>主要痛点</span>{positioning.painPoints.slice(0,4).map((x,i)=><p key={i}>{i+1}. {zhText(x)}</p>)}</div>
              <div><span>购买动机</span>{positioning.motivations.slice(0,4).map((x,i)=><p key={i}>{i+1}. {zhText(x)}</p>)}</div>
            </div>
            <div className="confidence-line"><span>AI 推导 Confidence</span><strong>{num(positioning.confidence)}/100</strong></div>
          </section>}

          <section className="panel">
            <div className="panel-title-row"><div><h2>分析运行状态</h2><p>{running?'当前 Run 正在执行；再次点击分析会取消这一轮。':'每次分析都有独立 Run，单个 Provider 失败不会让整份报告消失。'}</p></div><span className={'run-badge '+(running?'is-running':'')}>{running?'RUNNING':result?result.runStatus.toUpperCase():'IDLE'}</span></div>
            {running&&!result&&<div className="skeleton-stack"><div className="skeleton-line"/><div className="skeleton-line short"/></div>}
            {result&&<><div className="run-meta"><span>Run ID: <code>{result.runId.slice(0,8)}</code></span><span>完成：{new Date(result.completedAt).toLocaleString()}</span></div>
              <div className="module-grid">{Object.entries(result.modules).map(([k,v])=><div className="module-row" key={k}><span>{moduleNames[k]||k}</span><span className={'status-pill status-'+v.status}>{labels[v.status]}</span></div>)}</div></>}
          </section>

          {result?.modules.website?.status==='partial'&&<section className="panel error-panel" role="status"><h2>网站限制了自动采集 · 本次仅部分可用</h2><p>首页请求受到限制，系统已分别尝试商品目录、Google 搜索、公开广告和历史已关联的社媒账号。各模块是否有数据独立判断；未验证的销售和流量仍保持空值。</p><p>技术原因：{result.modules.website.error}</p>{nextAttempt&&<p><strong>下一次计划重新尝试的时间：{retryTime}</strong>。在此之前重复点击不会产生新数据。</p>}</section>}

          {state.matches('failure')&&<section className="panel error-panel"><h2>本次分析失败</h2><p>{state.context.error}</p><p>可以直接重新分析；旧请求不会覆盖新结果。</p></section>}

          {result&&<section className="panel">
            <div className="panel-title-row"><div><h2>本次分析完整性</h2><p>Observed / Estimated / Derived 区分来源；受限的首页不能阻止独立公开信号查询。</p></div><strong>成功采集 {completed}/{Object.keys(result.modules).length}</strong></div>
            <div className="quality-grid">
              <div><span>网站</span><strong>{result.website.website.finalUrl||result.url}</strong></div>
              <div><span>Snapshot</span><strong>{result.snapshotObservedAt?'已保存':'未保存'}</strong></div>
              <div><span>Catalog</span><strong>{productCount??'待校验'}</strong></div>
            </div>
          </section>}
        </>}

        {active==='流量'&&<>
          <div className="page-heading split-heading"><div><h2>流量情报</h2><p>不是第一方 Analytics；使用站点规模、SEO 完整度和 Google SERP 采样构建 Low / Base / High 区间。</p></div><DataTag type="estimated"/></div>
          {!result?<EmptyModule title="尚未分析网站" detail="先输入竞品域名并完成一次分析。"/>:
           !traffic?<EmptyModule title="Traffic 模型本次不可用" detail={result.modules.metrics?.error||'Metrics Provider 没有返回可验证的流量估算。'}/>:
           <>
             <div className="estimate-band">
               <Metric label="Monthly Low" value={num(traffic.low)} note="保守情景"/>
               <Metric label="Monthly Base" value={num(traffic.base)} note="基准情景"/>
               <Metric label="Monthly High" value={num(traffic.high)} note="高位情景"/>
               <Metric label="模型输入评分（非准确率）" value={num(traffic.confidence)+'/100'} note={traffic.modelVersion}/>
             </div>
             <section className="panel">
               <div className="panel-title-row"><div><h2>估算依据</h2><p>这里只显示模型真正使用的输入，不把模型结果伪装成已观测访客数据。</p></div><DataTag type="derived"/></div>
               <div className="evidence-grid">
                 <div><span>完整商品数</span><strong>{num(traffic.inputs.productCount)}</strong></div>
                 <div><span>Sitemap 发现 URL</span><strong>{num(traffic.inputs.sitemapUrls??traffic.inputs.indexedUrls)}</strong></div>
                 <div><span>Blog 页面</span><strong>{num(traffic.inputs.blogCount)}</strong></div>
                 <div><span>Collection 页面</span><strong>{num(traffic.inputs.collectionCount)}</strong></div>
                 <div><span>SEO 完整度</span><strong>{pct(traffic.inputs.seoCompleteness,0)}</strong></div>
                 <div><span>SERP 采样词</span><strong>{num(traffic.inputs.serpQueries)}</strong></div>
                 <div><span>90 天更新 URL</span><strong>{num(traffic.inputs.sitemapFreshness?.updated90d)}</strong></div>
                 <div><span>Lastmod 覆盖</span><strong>{traffic.inputs.sitemapFreshness?.coverageRatio==null?'—':pct(traffic.inputs.sitemapFreshness.coverageRatio,0)}</strong></div>
               </div>
               {traffic.inputs.note&&<div className="model-note">{traffic.inputs.note}</div>}
             </section>
             <section className="panel">
               <div className="panel-title-row"><div><h2>历史基础</h2><p>后续每次监控都会积累新的估算快照；目前不会把缺失历史日期用当前值补齐。</p></div><span className="history-count">{trafficHistory} 条 Traffic 快照</span></div>
             </section>
           </>}
        </>}

        {active==='销售'&&<>
          <div className="page-heading split-heading"><div><h2>销售额情报</h2><p>Revenue = Traffic × CVR 区间 × 加权 AOV。公开站点无法直接暴露完整订单，因此只提供 Low / Base / High。</p></div><DataTag type="estimated"/></div>
          {!result?<EmptyModule title="尚未分析网站" detail="先完成一次分析，再查看销售额模型。"/>:
           !revenue?<EmptyModule title="Revenue 模型本次不可用" detail={result.modules.metrics?.error||'Metrics Provider 没有返回可验证的销售额估算。'}/>:
           <>
             <div className="estimate-band">
               <Metric label="Monthly Revenue · Low" value={money(revenue.revenue.low,currency)} note={num(revenue.orders.low,1)+' 单'}/>
               <Metric label="Monthly Revenue · Base" value={money(revenue.revenue.base,currency)} note={num(revenue.orders.base,1)+' 单'}/>
               <Metric label="Monthly Revenue · High" value={money(revenue.revenue.high,currency)} note={num(revenue.orders.high,1)+' 单'}/>
               <Metric label="模型输入评分（非准确率）" value={num(revenue.confidence)+'/100'} note={revenue.modelVersion}/>
             </div>
             <section className="panel">
               <div className="panel-title-row"><div><h2>转化与客单价假设</h2><p>这些是模型参数，不是竞品后台的真实订单数据。</p></div><DataTag type="derived"/></div>
               <div className="evidence-grid">
                 <div><span>AOV · Base</span><strong>{money(revenue.aov,currency)}</strong></div>
                 <div><span>CVR · Low</span><strong>{pct(revenue.cvr.low,2)}</strong></div>
                 <div><span>CVR · Base</span><strong>{pct(revenue.cvr.base,2)}</strong></div>
                 <div><span>CVR · High</span><strong>{pct(revenue.cvr.high,2)}</strong></div>
                 <div><span>商品中位价输入</span><strong>{money(revenue.inputs?.medianCatalogPrice,currency)}</strong></div>
                 <div><span>AOV 区间</span><strong>{revenue.aovRange?money(revenue.aovRange.low,currency)+' – '+money(revenue.aovRange.high,currency):'—'}</strong></div>
                 <div><span>历史 Revenue 快照</span><strong>{num(revenueHistory)}</strong></div>
               </div>
             </section>
             <section className="panel caution-panel">
               <div className="panel-title-row"><div><h2>解释边界</h2><p>Low / Base / High 是区间估算。没有访问竞品 Shopify Orders、GA4 或支付数据时，不会把这些数字标成“已观测销售额”。</p></div><DataTag type="estimated"/></div>
             </section>
           </>}
        </>}

        {active==='经营效率'&&<>
          <div className="page-heading split-heading"><div><h2>经营效率</h2><p>根据销售额模型与品类毛利先验推导 Gross Margin / Gross Profit；广告效率必须等待可靠 Ad Spend 输入。</p></div><DataTag type="derived"/></div>
          {!result?<EmptyModule title="尚未分析网站" detail="先完成一次分析，再查看经营效率。"/>:
           !economics?<EmptyModule title="Economics 模型本次不可用" detail={result.modules.metrics?.error||'Metrics Provider 没有返回可验证的经营效率估算。'}/>:
           <>
             <div className="metric-grid">
               <Metric label="Gross Margin · Base" value={pct(economics.grossMargin.base,0)} note={'Low '+pct(economics.grossMargin.low,0)+' · High '+pct(economics.grossMargin.high,0)}/>
               <Metric label="Gross Profit · Base" value={money(economics.grossProfit.base,currency)} note={'Low '+money(economics.grossProfit.low,currency)+' · High '+money(economics.grossProfit.high,currency)}/>
               <Metric label="MER" value={economics.mer==null?'—':num(economics.mer,2)} note={economics.mer==null?'等待广告花费模型':'推导'}/>
               <Metric label="CAC" value={economics.cac==null?'—':money(economics.cac,currency)} note={economics.cac==null?'等待付费媒体证据':'推导'}/>
             </div>
             <section className="panel">
               <div className="panel-title-row"><div><h2>模型状态</h2><p>{economics.note||'经营效率模型使用公开信号与品类先验。'}</p></div><span className="history-count">{economicsHistory} 条 Economics 快照</span></div>
               <div className="evidence-grid">
                 <div><span>模型假设评分（非准确率）</span><strong>{num(economics.confidence)}/100</strong></div>
                 <div><span>Model</span><strong>{economics.modelVersion}</strong></div>
                 <div><span>Paid ROAS</span><strong>{economics.paidRoas==null?'—':num(economics.paidRoas,2)}</strong></div>
               </div>
             </section>
             <section className="panel caution-panel">
               <div className="panel-title-row"><div><h2>为什么 MER / CAC 仍为空</h2><p>“发现广告”只能证明广告活动存在，不能证明月花费。V2 不会把广告数量直接转换成虚假预算。</p></div><DataTag type="derived"/></div>
             </section>
           </>}
        </>}

        {active==='SEO'&&<>
          <div className="page-heading split-heading"><div><h2>SEO 情报</h2><p>Sitemap/页面结构属于已观测数据；SERP 排名是按当前查询样本采集，不等于完整关键词数据库。</p></div><DataTag type="observed"/></div>
          {independentSearch?.seo&&<section className="panel" aria-label="独立 Google 搜索信号">
            <div className="panel-title-row"><div><h2>独立搜索引擎采样</h2>
              <p>无需访问网站首页。采集于 {independentSearch.observedAt?new Date(independentSearch.observedAt).toLocaleString('zh-CN'):'未知'}；采集来源：{independentSearch.seo.classification==='bing_rss_search_sample'?'Bing RSS':independentSearch.seo.classification==='brave_web_search_sample'?'Brave Search API':'Google SerpApi'}。仅代表本次抽样，不代表全部索引页、真实流量或商业关键词排名。</p></div><DataTag type="observed"/></div>
            <div className="metric-grid">
              <Metric label="本次搜索发现的站内页面" value={num(independentSearch.seo.searchResults.resultsInSample)} note="仅本次 Google site: 查询样本"/>
              <Metric label="检查搜索结果" value={num(independentSearch.seo.searchResults.topResultsChecked)} note="本次抽样数量，非总索引数"/>
            </div>
            {independentSearch.seo.searchResults.pages.length?<div className="table-wrap"><table className="data-table"><thead><tr><th>网页标题</th><th>链接</th><th>摘要</th></tr></thead><tbody>
            {independentSearch.seo.searchResults.pages.slice(0,10).map((p,i)=><tr key={p.url+i}><td>{p.title||'—'}</td><td><a href={p.url} target="_blank" rel="noreferrer">查看公开页面 ↗</a></td><td>{p.snippet||'—'}</td></tr>)}
            </tbody></table></div>:<div className="empty-inline">本次 Google 搜索样本未发现目标域名页面；不等于网站没有被收录。</div>}
          </section>}
                    {!result?<EmptyModule title="尚未分析网站" detail="先完成一次分析，再查看 SEO 采样。"/>:
           !seo?<EmptyModule title={independentSearch?'网站内部 SEO 扫描不可用，以上是独立搜索结果':'SEO 数据本次不可用'} detail={independentSearch?'首页受限，未测量 Sitemap、Title、H1 或真实自然搜索流量。':(result?.search?.limitations?.join(' ')||result.modules.metrics?.error||'无法获得公开搜索采样。')}/>:
           <>
             <div className="metric-grid">
               <Metric label="Sitemap 发现 URL" value={num(seo.sitemapUrls??seo.indexedUrls)} note={<><DataTag type="observed"/> 不等于 Google 已收录</>}/>
               <Metric label="SEO 采样页面" value={num(seo.pagesSampled)} note="标题 / Description / H1"/>
               <Metric label="页面 SEO 完整度" value={seoCompletion==null?'—':pct(seoCompletion,0)} note={<DataTag type="derived"/>}/>
               <Metric label="SEO 信号评分（非准确率）" value={seo.confidence==null?'—':num(seo.confidence)+'/100'} note={seo.serp?.providerConnected?'SerpApi 已参与':'无 SerpApi 样本'}/>
               <Metric label="近 30 天更新 URL" value={num(seo.sitemapFreshness?.updated30d)} note={<DataTag type="observed"/>}/>
               <Metric label="近 90 天更新 URL" value={num(seo.sitemapFreshness?.updated90d)} note={<DataTag type="observed"/>}/>
               <Metric label="Lastmod 覆盖率" value={seo.sitemapFreshness?.coverageRatio==null?'—':pct(seo.sitemapFreshness.coverageRatio,0)} note="Sitemap lastmod"/>
               <Metric label="最近 Sitemap 更新" value={seo.sitemapFreshness?.latestLastmod?new Date(seo.sitemapFreshness.latestLastmod).toLocaleDateString():'—'} note="公开 lastmod"/>
             </div>

             <section className="panel">
               <div className="panel-title-row"><div><h2>Google SERP 采样</h2><p>只报告实际查询过的关键词与排名。未出现在样本 Top 20 的词显示为“未发现”。</p></div><DataTag type="observed"/></div>
               <div className="seo-summary">
                 <div><span>采样查询</span><strong>{num(seo.serp?.samples.length||0)}</strong></div>
                 <div><span>进入 Top 20</span><strong>{num(seo.serp?.appearances||0)}</strong></div>
                 <div><span>最佳排名</span><strong>{seo.serp?.bestRank?'#'+seo.serp.bestRank:'—'}</strong></div>
                 <div><span>平均排名</span><strong>{seo.serp?.avgRank?'#'+seo.serp.avgRank:'—'}</strong></div>
               </div>
               {seo.serp?.samples.length
                 ?<div className="table-wrap"><table className="data-table"><thead><tr><th>查询词</th><th>观测排名</th><th>Google 结果量信号</th></tr></thead><tbody>
                   {seo.serp.samples.map((row,i)=><tr key={row.query+'-'+i}><td>{row.query}</td><td>{row.rank?'#'+row.rank:'Top 20 未发现'}</td><td>{num(row.totalResults)}</td></tr>)}
                 </tbody></table></div>
                 :<div className="empty-inline">当前没有可读取的 SERP 样本。</div>}
             </section>

             <section className="panel">
               <div className="panel-title-row"><div><h2>页面 SEO 基础</h2><p>来自实际采样页面，不推断未读取页面。</p></div><span className="history-count">{seoHistory} 条 SEO 快照</span></div>
               <div className="evidence-grid">
                 <div><span>有 Title</span><strong>{num(seo.pagesWithTitles)} / {num(seo.pagesSampled)}</strong></div>
                 <div><span>有 Meta Description</span><strong>{num(seo.pagesWithDescriptions)} / {num(seo.pagesSampled)}</strong></div>
                 <div><span>有 H1</span><strong>{num(seo.pagesWithH1)} / {num(seo.pagesSampled)}</strong></div>
                 <div><span>SERP Visibility</span><strong>{seo.serp?num(seo.serp.visibilityScore,3):'—'}</strong></div>
               </div>
             </section>
           </>}
        </>}

        {active==='竞品发现'&&<CompetitorDiscoveryPanel url={result?.url||''} onAnalyze={analyzeDomain}/>}
        {active==='付费广告'&&<PaidAdsPanel data={result?.ads||null}/>}
        {active==='社媒'&&<SocialPanel data={result?.social||null}/>}
        {active==='内容素材'&&<CreativePanel data={result?.strategy||null}/>}
        {active==='竞品监控'&&<WatchlistPanel current={result?{domain:result.domain,url:result.url,name:result.website.website.title||result.domain,platform:result.website.technology?.platform||null}:null} onAnalyze={analyzeDomain}/>}

        {active==='产品'&&<section className="panel">
          <div className="panel-title-row"><div><h2>产品情报</h2><p>V2 只展示通过完整目录校验的数据。</p></div><DataTag type="observed"/></div>
          {!p?<div className="empty-state">本次无法读取完整商品目录，历史记录请到「总览」或「变化记录」查看。</div>:<div className="product-summary">
            <Metric label="商品数" value={productCount??'仅发现部分商品，未验证总数'}/>
            <Metric label="中位价" value={money(p.medianPrice,currency)}/>
            <Metric label="完整观测范围" value={p.priceRange?money(p.priceRange.min,currency)+' – '+money(p.priceRange.max,currency):'—'}/>
          </div>}
        </section>}

        {active==='变化记录'&&<>
          <div className="page-heading split-heading"><div><h2>历史趋势</h2><p>按日 / 月 / 年查看真实快照；MoM / YoY 只在存在对应历史周期时计算，不使用当前值回填过去。</p></div><DataTag type="observed"/></div>
          <section className="panel history-controls">
            <div><span>时间范围</span><div className="segmented">{([['30d','30天'],['90d','90天'],['1y','1年'],['all','全部']] as const).map(([v,l])=><button key={v} className={historyRange===v?'active':''} onClick={()=>setHistoryRange(v)}>{l}</button>)}</div></div>
            <div><span>时间粒度</span><div className="segmented">{([['daily','日'],['monthly','月'],['yearly','年']] as const).map(([v,l])=><button key={v} className={historyGrain===v?'active':''} onClick={()=>setHistoryGrain(v)}>{l}</button>)}</div></div>
          </section>
          {history.isLoading&&<section className="panel"><div className="empty-state">正在聚合历史数据…</div></section>}
          {history.isError&&<section className="panel"><div className="empty-state error-text">历史读取失败</div></section>}
          {history.data?.trends&&<>
            <div className="trend-grid">
              <TrendMetricCard title="流量 · Base" trend={history.data.trends.traffic} format={v=>v==null?'—':num(v)}/>
              <TrendMetricCard title="销售额 · Base" trend={history.data.trends.revenue} format={v=>v==null?'—':money(v,currency)}/>
              <TrendMetricCard title="公开商品数" trend={history.data.trends.productCount} format={v=>v==null?'—':num(v)}/>
              <TrendMetricCard title="Sitemap URL" trend={history.data.trends.seoSitemapUrls} format={v=>v==null?'—':num(v)}/>
              <TrendMetricCard title="毛利率 · Base" trend={history.data.trends.grossMargin} format={v=>v==null?'—':pct(v,0)}/>
            </div>
            <section className="panel">
              <div className="panel-title-row"><div><h2>社媒粉丝趋势</h2><p>各平台单独成序列，不把不同平台粉丝数强行相加。</p></div><span className="history-count">{history.data.meta?.bucketCount??0} 个周期</span></div>
              {history.data.trends.socialFollowers.length?<div className="social-trend-list">{history.data.trends.socialFollowers.map(s=><div key={s.series}><strong>{s.series}</strong><span>{s.latest?.value==null?'—':num(s.latest.value)}</span><small>环比 {delta(s.latest?.momPct)} · 同比 {delta(s.latest?.yoyPct)}</small></div>)}</div>:<div className="empty-inline">暂无可用粉丝历史。</div>}
            </section>
            <section className="panel">
              <div className="panel-title-row"><div><h2>趋势解释边界</h2><p>当前数据从 Insight Compass 开始保存快照之后才形成时间序列。跨月或跨年样本不足时，MoM / YoY 保持为空，不会用模型补出不存在的历史。</p></div><span className="data-tag data-derived">可信边界</span></div>
            </section>
          </>}
        </>}

      </section>
    </main>
  </div>;
}
